import { differenceInCalendarDays } from 'date-fns'

import type {
	EstimateMethodType,
	FieldType,
	ICollection,
	IQuote,
	IQuoteDocumentTextLine,
	IQuoteLine
} from '@/interfaces'
import { Collection, QuoteDocumentTextLine, QuoteLine, QuoteTextLine } from '@/models'

import { addToJSON } from './helper'

export class Quote implements IQuote {
	[key: string]: FieldType
	id: number | string
	quoteId: number | string
	subjobId: number
	subjobNumber: string
	clientCode: string
	clientName: string
	clientContact?: string
	clientContactId?: string
	clientContactName?: string
	location: string
	address?: string
	showDate: Date
	quoteDate: Date
	docNumber?: string
	revision: string
	headerComments: string
	footerComments: string
	estimatorNotes?: string
	submissionNotes?: string
	closingNotes?: string

	// State Management
	quoteState: number
	quoteStateAlias: string
	quoteStatus: 'Open' | 'Closed' | 'Canceled'

	// Financial
	quoteTotal: number
	totalTax?: number
	totalCost?: number

	// Workflow
	workOrderId?: number
	createDate: Date
	createBy: number
	createByName: string
	lastUpdateDate: Date
	lastUpdateBy: number
	lastModified?: Date
	updateDate: Date
	updateBy?: number
	createByEmail?: string
	updateByEmail?: string
	createDateTime?: Date
	updateDateTime?: Date

	// Line Items
	quoteLines: ICollection<IQuoteLine>

	// Document-level text lines (headers)
	documentTextLines: ICollection<IQuoteDocumentTextLine>

	// Job Classification
	jobType: string
	isCostJobType: boolean

	// People
	salespersonId?: number
	salespersonName?: string
	salesTeamName?: string

	// Approval
	approvalRequired: boolean
	approvedBy?: number
	approvedDate?: Date

	// Subjob dates
	kickDate?: Date
	shipDate?: Date

	// SCR status
	scrStatus?: 'Approved' | 'Pending' | 'Not Required'
	scrPending: boolean

	// Estimate Method
	estimateMethod: EstimateMethodType | null

	// Summary fields
	lineCount?: number
	hasAlternatives?: boolean
	hasTax?: boolean

	// Computed fields
	hash?: string
	displayName?: string

	constructor({
		id,
		quoteId,
		subjobId,
		subjobNumber,
		clientCode,
		clientId, // legacy field
		clientName,
		clientContact,
		clientContactId,
		clientContactName,
		location,
		address,
		showDate,
		quoteDate,
		docNumber,
		revision,
		headerComments,
		footerComments,
		estimatorNotes,
		submissionNotes,
		closingNotes,
		// State Management
		quoteState,
		quoteStateAlias,
		quoteStatus,
		// Financial
		quoteTotal,
		totalTax,
		// Workflow
		workOrderId,
		createDate,
		createBy,
		createByName,
		lastUpdateDate,
		lastUpdateBy,
		lastModified,
		// Line Items
		quoteLines,
		// Document-level text lines
		documentTextLines,
		// Job Classification
		jobType,
		isCostJobType,
		// Approval
		approvalRequired,
		approvedBy,
		approvedDate,
		// Subjob dates
		kickDate,
		shipDate,
		// SCR status
		scrStatus,
		scrPending,
		// Estimate Method
		estimateMethod,
		// Additional tracking fields
		updateDate,
		updateBy,
		createByEmail,
		updateByEmail,
		createDateTime,
		updateDateTime,
		// Financial
		totalCost,
		// People
		salespersonId,
		salespersonName,
		salesTeamName,
		// Summary fields
		lineCount,
		hasAlternatives,
		hasTax,
		// Computed fields
		hash,
		displayName
	}: Partial<IQuote> = {}) {
		this.id = id || 0
		this.quoteId = quoteId || id || 0
		this.subjobId = subjobId || 0
		this.subjobNumber = subjobNumber || ''
		this.clientCode = clientCode || String(clientId || '') || ''
		this.clientName = clientName || ''
		this.clientContact = clientContact
		this.clientContactId = clientContactId
		this.clientContactName = clientContactName
		this.location = location || ''
		this.address = address
		this.showDate = showDate ? new Date(showDate) : new Date()
		this.quoteDate = quoteDate ? new Date(quoteDate) : new Date()
		this.docNumber = docNumber
		this.revision = revision || '1.0'
		this.headerComments = headerComments || ''
		this.footerComments = footerComments || ''
		this.estimatorNotes = estimatorNotes
		this.submissionNotes = submissionNotes
		this.closingNotes = closingNotes

		// State Management
		this.quoteState = quoteState || 0
		this.quoteStateAlias = quoteStateAlias || 'New'
		this.quoteStatus = quoteStatus || 'Open'

		// Financial
		this.quoteTotal = quoteTotal || 0
		// Only set totalTax if provided to preserve original data structure
		if (totalTax !== undefined) {
			this.totalTax = totalTax
		}
		if (totalCost !== undefined && totalCost !== null) {
			this.totalCost = totalCost as number
		}

		// Workflow
		this.workOrderId = workOrderId
		this.createDate = createDate ? new Date(createDate) : new Date()
		this.createBy = createBy || 0
		this.createByName = createByName || ''
		this.lastUpdateDate = lastUpdateDate ? new Date(lastUpdateDate) : new Date()
		this.lastUpdateBy = lastUpdateBy || 0
		this.lastModified = lastModified ? new Date(lastModified) : undefined
		this.updateDate = updateDate ? new Date(updateDate as string | number | Date) : new Date()
		this.updateBy = updateBy as number | undefined
		this.createByEmail = createByEmail as string | undefined
		this.updateByEmail = updateByEmail as string | undefined
		this.createDateTime = createDateTime ? new Date(createDateTime) : undefined
		this.updateDateTime = updateDateTime ? new Date(updateDateTime) : undefined

		// Line Items
		this.quoteLines = new Collection<IQuoteLine>(QuoteLine, quoteLines || [])

		// Document-level text lines (headers)
		this.documentTextLines = new Collection<IQuoteDocumentTextLine>(QuoteDocumentTextLine, documentTextLines || [])

		// Job Classification
		this.jobType = jobType || ''
		this.isCostJobType = isCostJobType || false

		// People
		this.salespersonId = salespersonId as number | undefined
		this.salespersonName = salespersonName as string | undefined
		this.salesTeamName = salesTeamName as string | undefined

		// Approval
		this.approvalRequired = approvalRequired || false
		this.approvedBy = approvedBy
		this.approvedDate = approvedDate ? new Date(approvedDate) : undefined

		// Subjob dates
		this.kickDate = kickDate ? new Date(kickDate) : undefined
		this.shipDate = shipDate ? new Date(shipDate) : undefined

		// SCR status
		this.scrStatus = scrStatus || undefined
		this.scrPending = scrPending || false

		// Estimate Method
		this.estimateMethod = estimateMethod || null

		// Summary fields
		this.lineCount = lineCount as number | undefined
		this.hasAlternatives = hasAlternatives as boolean | undefined
		this.hasTax = hasTax as boolean | undefined

		// Computed fields
		this.hash = hash as string | undefined
		this.displayName = displayName as string | undefined

		addToJSON(Quote)
	}

	// Calculated fields
	get staleDays(): number {
		const today = new Date()
		return differenceInCalendarDays(today, this.createDate)
	}

	get woGenerated(): boolean {
		return !!this.workOrderId && this.workOrderId > 0
	}

	public calculateTotal(): number {
		return this.quoteLines.reduce((total, line) => {
			if (!line.isDeleted && !line.isAlternative && !line.isTax) {
				return total + (line.price || 0)
			}
			return total
		}, 0)
	}

	public calculateTotalTax(): number {
		return this.quoteLines.reduce((total, line) => {
			if (!line.isDeleted && !line.isAlternative && line.isTax) {
				return total + (line.price || 0)
			}
			return total
		}, 0)
	}

	public canTransitionTo(targetState: number): boolean {
		const validTransitions: { [key: number]: number[] } = {
			0: [1], // New -> Created
			1: [2], // Created -> Estimating
			2: [3], // Estimating -> Estimated
			3: [4, 2], // Estimated -> Approval or back to Estimating
			4: [5, 6], // Approval -> Approved or Rejected
			5: [6], // Approved -> Sent
			6: [] // Sent (final state)
		}

		return validTransitions[this.quoteState]?.includes(targetState) || false
	}

	public getNextAvailableStates(): number[] {
		const validTransitions: { [key: number]: number[] } = {
			0: [1], // New -> Created
			1: [2], // Created -> Estimating
			2: [3], // Estimating -> Estimated
			3: [4, 2], // Estimated -> Approval or back to Estimating
			4: [5, 6], // Approval -> Approved or Rejected
			5: [6], // Approved -> Sent
			6: [] // Sent (final state)
		}

		return validTransitions[this.quoteState] || []
	}

	public addLine(lineData?: Partial<IQuoteLine>): IQuoteLine {
		const nextLineNumber = Math.max(0, ...this.quoteLines.map((l) => l.lineNum || 0)) + 1
		const newLine = this.quoteLines.add(
			new QuoteLine({
				id: 0,
				lineId: 0,
				quoteId: this.quoteId,
				lineNum: nextLineNumber,
				item: lineData?.item || '',
				itemCode: lineData?.itemCode,
				rev: lineData?.rev || '1.0',
				description: lineData?.description || '',
				cost: lineData?.cost,
				price: lineData?.price || 0,
				isAlternative: lineData?.isAlternative || false,
				isDeleted: false,
				textLines: new Collection(QuoteTextLine, [])
			})
		)

		this.updateTotal()
		return newLine
	}

	public removeLine(lineId: number): void {
		const line = this.quoteLines.find((l) => l.lineId === lineId)
		if (line) {
			this.quoteLines.remove(line)
			// Resequence remaining lines
			this.quoteLines.forEach((l, index) => {
				l.lineNum = index + 1
			})
			this.updateTotal()
		}
	}

	public updateTotal(): void {
		this.quoteTotal = this.calculateTotal()

		// Only update totalTax if there are tax lines to preserve data structure
		const taxTotal = this.calculateTotalTax()
		if (taxTotal > 0 || this.totalTax !== undefined) {
			this.totalTax = taxTotal
		}
	}

	// Document Text Lines Management
	public addDocumentTextLine(text: string): IQuoteDocumentTextLine {
		const maxOrder = Math.max(...this.documentTextLines.map((l) => l.orderNumber), 0)
		const newLine = this.documentTextLines.add({
			lineNum: this.documentTextLines.length,
			orderNumber: maxOrder + 1,
			lineType: 'TEXT',
			lineText: text,
			position: 'HEADER'
		} as IQuoteDocumentTextLine)

		return newLine
	}

	public removeDocumentTextLine(lineNum: number): void {
		const line = this.documentTextLines.find((l) => l.lineNum === lineNum)
		if (line) {
			this.documentTextLines.remove(line)
			// Resequence remaining lines
			this.documentTextLines.forEach((l, index) => {
				l.lineNum = index
				l.orderNumber = index + 1
			})
		}
	}

	public updateDocumentTextLine(lineNum: number, text: string): void {
		const line = this.documentTextLines.find((l) => l.lineNum === lineNum)
		if (line) {
			line.lineText = text
		}
	}

	public getHeaderLines(): IQuoteDocumentTextLine[] {
		return this.documentTextLines.filter((l) => l.position === 'HEADER')
	}

	public getActiveDocumentTextLines(): IQuoteDocumentTextLine[] {
		return this.documentTextLines.slice() // Return a copy of all lines since we're removing deleted ones
	}

	public hasDocumentTextLines(): boolean {
		return this.getActiveDocumentTextLines().length > 0
	}
}
